
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import App from "./App.tsx";
import "./index.css";

// Simplified initialization

// Clear image cache on app start to prevent stale images
import { imageCache } from './services/imageCache';
imageCache.clearAllCache();

// Force stylesheet reload to prevent stale CSS in preview
const forceStyleReload = () => {
  const links = document.querySelectorAll('link[rel="stylesheet"]');
  links.forEach(link => {
    const href = (link as HTMLLinkElement).href;
    // Skip external font stylesheets (fontshare, google fonts, etc.)
    if (href && !href.includes('_v=') && !href.includes('fontshare.com') && !href.includes('fonts.googleapis.com')) {
      const separator = href.includes('?') ? '&' : '?';
      (link as HTMLLinkElement).href = `${href}${separator}_v=${Date.now()}`;
    }
  });
};

// Execute on load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', forceStyleReload);
} else {
  forceStyleReload();
}

// Simplified QueryClient configuration for better performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      retry: 1, // Simple retry
    },
    mutations: {
      retry: 0, // No mutation retries
    },
  },
});

// Hide loading skeleton once React starts rendering
document.body.classList.add('react-loaded');

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>
);
